<template>
  <div class="min-h-screen" data-theme="green">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="shared_runs" />

    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Flash -->
      <div v-if="flash" :class="['mb-4 px-4 py-3 rounded-lg text-sm', flash.type === 'success' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800']">
        {{ flash.message }}
      </div>

      <!-- Back link -->
      <a :href="locale === 'ru' ? '/ru/shared_runs' : '/shared_runs'" class="text-sm text-blue-700 hover:text-blue-900 mb-6 inline-block">&larr; {{ t('shared_runs.back_to_list') }}</a>

      <!-- Run details card -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <!-- Date & time -->
        <div class="flex items-center gap-3 mb-6">
          <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {{ formatDate(run.date) }}
          </span>
          <span class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {{ formatTime(run.date) }}
          </span>
        </div>

        <!-- Stats -->
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-6 mb-6">
          <div v-if="run.distance">
            <p class="text-xs text-gray-500 uppercase tracking-wide">{{ t('shared_runs.distance') }}</p>
            <p class="text-xl font-bold text-gray-900">{{ run.distance }} <span class="text-sm font-normal text-gray-500">{{ t('shared_runs.km') }}</span></p>
          </div>
          <div v-if="run.pace">
            <p class="text-xs text-gray-500 uppercase tracking-wide">{{ t('shared_runs.pace') }}</p>
            <p class="text-xl font-bold text-gray-900">{{ run.pace }} <span class="text-sm font-normal text-gray-500">{{ t('shared_runs.min_km') }}</span></p>
          </div>
          <div>
            <p class="text-xs text-gray-500 uppercase tracking-wide">{{ t('shared_runs.organizer') }}</p>
            <p v-if="signedIn && run.organizer.id !== userId" @click="openChatWith(run.organizer)" class="text-lg font-bold text-gray-900 hover:text-blue-700 cursor-pointer">{{ run.organizer.name }}</p>
            <p v-else class="text-lg font-bold text-gray-900">{{ run.organizer.name }}</p>
            <!-- Organizer toggle phone -->
            <button v-if="run.organizer.id === userId && run.organizer_has_phone"
                    @click="toggleOrganizerPhone"
                    :class="['mt-1 text-xs cursor-pointer', run.phone_visible ? 'text-blue-700' : 'text-gray-400']">
              {{ run.phone_visible ? t('shared_runs.phone_shown') : t('shared_runs.phone_hidden') }}
            </button>
            <!-- Participant sees organizer phone -->
            <a v-if="run.organizer.id !== userId && run.organizer.phone && isParticipant"
               :href="'tel:' + run.organizer.phone"
               class="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
              {{ run.organizer.phone }}
            </a>
          </div>
        </div>

        <!-- Map -->
        <div v-if="run.latitude && run.longitude" class="mb-6 rounded-lg border border-gray-200 overflow-hidden map-container">
          <div ref="mapContainer" style="height: 300px; width: 100%;"></div>
        </div>

        <!-- Actions -->
        <div class="flex gap-3 mb-6">
          <template v-if="!isPast">
            <template v-if="signedIn">
              <button v-if="!isParticipant && run.organizer.id !== userId" @click="joinRun"
                      class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition">
                {{ t('shared_runs.join') }}
              </button>
              <button v-if="isParticipant" @click="leaveRun"
                      class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
                {{ t('shared_runs.leave') }}
              </button>
            </template>
            <a v-else href="/users/sign_up"
               class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition inline-block">
              {{ t('shared_runs.register_to_participate') }}
            </a>
          </template>
        </div>

        <!-- Participants -->
        <div>
          <h3 class="text-sm font-semibold text-gray-900 mb-3">{{ t('shared_runs.participants') }} ({{ run.participants.length }})</h3>
          <ul v-if="run.participants.length > 0" class="space-y-2">
            <li v-for="p in run.participants" :key="p.id" class="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
              <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-sm font-medium text-blue-800 shrink-0">
                {{ (p.first_name || p.name).charAt(0).toUpperCase() }}
              </span>
              <span v-if="signedIn && p.id !== userId" @click="openChatWith({ id: p.id, name: p.name })" class="text-sm text-gray-900 hover:text-blue-700 cursor-pointer">{{ p.name }}</span>
              <span v-else class="text-sm text-gray-900">{{ p.name }}</span>
              <!-- Participant toggles own phone -->
              <button v-if="p.id === userId && p.has_phone"
                      @click="toggleParticipantPhone"
                      :class="['text-xs cursor-pointer', p.phone_visible ? 'text-blue-700' : 'text-gray-400']">
                {{ p.phone_visible ? t('shared_runs.phone_shown') : t('shared_runs.phone_hidden') }}
              </button>
              <!-- Organizer sees phone if consented -->
              <a v-if="p.phone && run.organizer.id === userId"
                 :href="'tel:' + p.phone"
                 class="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                {{ p.phone }}
              </a>
            </li>
          </ul>
          <p v-else class="text-sm text-gray-400">{{ t('shared_runs.no_participants') }}</p>
        </div>

        <!-- Comments -->
        <div class="mt-6 pt-6 border-t border-gray-200">
          <!-- Tabs -->
          <div class="flex gap-1 mb-4">
            <button @click="commentTab = 'public'"
                    :class="['px-4 py-2 text-sm font-medium rounded-lg transition', commentTab === 'public' ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200']">
              {{ t('shared_runs.public_comments') }} ({{ publicComments.length }})
            </button>
            <button v-if="isParticipantOrOrganizer" @click="commentTab = 'private'"
                    :class="['px-4 py-2 text-sm font-medium rounded-lg transition', commentTab === 'private' ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200']">
              {{ t('shared_runs.private_comments') }} ({{ privateComments.length }})
            </button>
          </div>

          <!-- Comment list -->
          <div v-if="activeComments.length > 0" class="space-y-4 mb-6">
            <div v-for="c in activeComments" :key="c.id" class="flex gap-3">
              <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-sm font-medium text-blue-800 shrink-0 mt-0.5">
                {{ c.user.name.charAt(0).toUpperCase() }}
              </span>
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <span v-if="signedIn && c.user.id !== userId" @click="openChatWith(c.user)" class="text-sm font-medium text-gray-900 hover:text-blue-700 cursor-pointer">{{ c.user.name }}</span>
                  <span v-else class="text-sm font-medium text-gray-900">{{ c.user.name }}</span>
                  <span class="text-xs text-gray-400">{{ formatCommentDate(c.created_at) }}</span>
                  <button v-if="c.user.id === userId || userAdmin"
                          @click="deleteComment(c.id)"
                          class="text-xs text-red-400 hover:text-red-600 cursor-pointer ml-auto">
                    {{ t('shared_runs.delete') }}
                  </button>
                </div>
                <p class="text-sm text-gray-700 mt-0.5">{{ c.body }}</p>
              </div>
            </div>
          </div>
          <p v-else class="text-sm text-gray-400 mb-4">{{ t('shared_runs.no_comments') }}</p>

          <!-- Add comment form -->
          <div v-if="signedIn && (commentTab === 'public' || isParticipantOrOrganizer)">
            <form @submit.prevent="addComment" class="flex gap-3">
              <input v-model="newComment" type="text" :placeholder="t('shared_runs.add_comment')"
                     class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <button type="submit" :disabled="!newComment.trim()"
                      class="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50 cursor-pointer">
                {{ t('shared_runs.send') }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import L from 'leaflet'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
})

const props = defineProps({
  signedIn: Boolean,
  userName: String,
  userId: Number,
  userAdmin: Boolean,
  sharedRun: Object,
  csrfToken: String
})

const { t, locale } = useI18n()

const run = reactive({ ...props.sharedRun })
const comments = ref([...(props.sharedRun.comments || [])])
const newComment = ref('')
const commentTab = ref('public')
const showMenu = ref(false)
const logoSize = ref(120)
const flash = ref(null)
const mapContainer = ref(null)

const isPast = computed(() => new Date(run.date) < new Date())
const isParticipant = computed(() => run.participants.some(p => p.id === props.userId))
const isParticipantOrOrganizer = computed(() => isParticipant.value || run.organizer.id === props.userId)
const publicComments = computed(() => comments.value.filter(c => !c.private))
const privateComments = computed(() => comments.value.filter(c => c.private))
const activeComments = computed(() => commentTab.value === 'private' ? privateComments.value : publicComments.value)

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function showFlash(message, type = 'success') {
  flash.value = { message, type }
  setTimeout(() => { flash.value = null }, 4000)
}

async function request(url, method, body = null) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' }
  }
  if (body) opts.body = JSON.stringify(body)
  return fetch(url, opts)
}

function openChatWith(user) {
  window.dispatchEvent(new CustomEvent('sidebar:open-chat', { detail: { id: user.id, name: user.name } }))
}

function formatCommentDate(dateStr) {
  const d = new Date(dateStr)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function updateRun(data) {
  Object.assign(run, data)
  if (data.comments) comments.value = data.comments
}

async function joinRun() {
  try {
    const res = await request(`/shared_runs/${run.id}/join`, 'POST')
    if (res.ok) { updateRun(await res.json()); showFlash(t('shared_runs.joined')) }
  } catch { showFlash(t('shared_runs.error'), 'error') }
}

async function leaveRun() {
  try {
    const res = await request(`/shared_runs/${run.id}/leave`, 'DELETE')
    if (res.ok) { updateRun(await res.json()); showFlash(t('shared_runs.left')) }
  } catch { showFlash(t('shared_runs.error'), 'error') }
}

async function toggleOrganizerPhone() {
  try {
    const res = await request(`/shared_runs/${run.id}`, 'PATCH', { shared_run: { phone_visible: !run.phone_visible } })
    if (res.ok) updateRun(await res.json())
  } catch { showFlash(t('shared_runs.error'), 'error') }
}

async function addComment() {
  if (!newComment.value.trim()) return
  try {
    const isPrivate = commentTab.value === 'private'
    const res = await request(`/shared_runs/${run.id}/comments`, 'POST', { comment: { body: newComment.value, private: isPrivate } })
    if (res.ok) {
      const data = await res.json()
      comments.value.push(data)
      newComment.value = ''
    }
  } catch { showFlash(t('shared_runs.error'), 'error') }
}

async function deleteComment(commentId) {
  try {
    const res = await request(`/shared_runs/${run.id}/comments/${commentId}`, 'DELETE')
    if (res.ok) {
      comments.value = comments.value.filter(c => c.id !== commentId)
    }
  } catch { showFlash(t('shared_runs.error'), 'error') }
}

async function toggleParticipantPhone() {
  try {
    const res = await request(`/shared_runs/${run.id}/toggle_phone`, 'PATCH')
    if (res.ok) updateRun(await res.json())
  } catch { showFlash(t('shared_runs.error'), 'error') }
}

onMounted(() => {
  if (run.latitude && run.longitude && mapContainer.value) {
    const map = L.map(mapContainer.value).setView([parseFloat(run.latitude), parseFloat(run.longitude)], 15)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(map)
    L.marker([parseFloat(run.latitude), parseFloat(run.longitude)]).addTo(map)
    setTimeout(() => map.invalidateSize(), 100)
  }
})
</script>
